/**
 * @fileoverview Storage adapter interface for abstracting storage implementations.
 */

/**
 * Generic storage adapter interface.
 * Allows for different storage backends (localStorage, sessionStorage, memory, etc.).
 */
export interface IStorageAdapter {
    /**
     * Get an item from storage.
     * @param key - Storage key
     * @returns Parsed value or null if not found
     */
    get(key: string): any | null;
    
    /**
     * Set an item in storage.
     * @param key - Storage key
     * @param value - Value to store (will be JSON stringified)
     */
    set(key: string, value: any): void;
    
    /**
     * Remove an item from storage.
     * @param key - Storage key
     */
    remove(key: string): void;
    
    /**
     * Clear all items from storage.
     */
    clear(): void;
}

/**
 * LocalStorage implementation of IStorageAdapter.
 */
export class LocalStorageAdapter implements IStorageAdapter {
    get(key: string): any | null {
        try {
            const item = localStorage.getItem(key);
            return item ? JSON.parse(item) : null;
        } catch (e) {
            console.warn(`Failed to get item from localStorage: ${key}`, e);
            return null;
        }
    }
    
    set(key: string, value: any): void {
        try {
            localStorage.setItem(key, JSON.stringify(value));
        } catch (e) {
            console.warn(`Failed to set item in localStorage: ${key}`, e);
        }
    }
    
    remove(key: string): void {
        try {
            localStorage.removeItem(key);
        } catch (e) {
            console.warn(`Failed to remove item from localStorage: ${key}`, e);
        }
    }
    
    clear(): void {
        try {
            localStorage.clear();
        } catch (e) {
            console.warn('Failed to clear localStorage', e);
        }
    }
}
